import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, X, Check, Sparkles, Flame, Trophy, AlertTriangle, HeartCrack } from "lucide-react";
import Confetti from "@/components/fx/Confetti";

// Ask permission once per browser session so streak reminders can fire.
function ensureNotificationPermission() {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  } catch {}
}

function fireBrowserNotification(title, body, tag) {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    const n = new Notification(title, {
      body,
      icon: "/logo192.png",
      badge: "/logo192.png",
      tag,
      renotify: false,
    });
    n.onclick = () => { window.focus(); n.close(); };
    setTimeout(() => { try { n.close(); } catch {} }, 8000);
  } catch {}
}

export default function SurpriseEventCard({ conversationId, wsEvent }) {
  const [event, setEvent] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completedBy, setCompletedBy] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [rewardFlash, setRewardFlash] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [streakGoal, setStreakGoal] = useState(14);
  const [atRisk, setAtRisk] = useState(false);
  const [streakLost, setStreakLost] = useState(0);
  const [lostDismissed, setLostDismissed] = useState(false);
  const [unlockedTheme, setUnlockedTheme] = useState(null);
  const [confettiKey, setConfettiKey] = useState(0);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!conversationId) return;
    const today = new Date().toISOString().slice(0, 10);
    const dkey = `surprise-dismissed-${conversationId}-${today}`;
    const lostKey = `streak-lost-ack-${conversationId}`;
    setDismissed(Boolean(localStorage.getItem(dkey)));
    setLostDismissed(false);
    notifiedRef.current = false;
    ensureNotificationPermission();

    (async () => {
      try {
        const { data } = await api.get(`/conversations/${conversationId}/surprise`);
        setEvent(data.event);
        setCompleted(!!data.completed);
        setCompletedBy(data.completed_by || null);
        setStreak(Number(data.quest_streak || 0));
        setStreakMax(Number(data.quest_streak_max || 0));
        setStreakGoal(Number(data.streak_goal || 14));
        setAtRisk(Boolean(data.streak_at_risk));
        const lost = Number(data.streak_lost || 0);
        if (lost > 0) {
          const ackedFor = localStorage.getItem(lostKey);
          if (ackedFor !== String(lost)) {
            setStreakLost(lost);
          }
        }

        // Streak-at-risk browser notification — max once per day per conversation
        if (data.streak_at_risk && !data.completed && Number(data.quest_streak || 0) > 0) {
          const nkey = `streak-risk-notif-${conversationId}-${data.date}`;
          if (!localStorage.getItem(nkey) && !notifiedRef.current) {
            notifiedRef.current = true;
            localStorage.setItem(nkey, "1");
            fireBrowserNotification(
              "⚠️ Serin bugün kırılabilir!",
              `${data.quest_streak} günlük görev serini kaybetmemek için günün görevini tamamla.`,
              `streak-risk-${conversationId}`
            );
          }
        }
      } catch {}
    })();
  }, [conversationId]);

  // Listen for WS surprise_completed event from other user
  useEffect(() => {
    if (!wsEvent) return;
    if (wsEvent.type === "surprise_completed" && wsEvent.conversation_id === conversationId) {
      setCompleted(true);
      setCompletedBy(wsEvent.completed_by || null);
      setAtRisk(false); // completed today → no longer at risk
      if (wsEvent.quest_streak != null) {
        setStreak(Number(wsEvent.quest_streak));
        setStreakMax((s) => Math.max(s, Number(wsEvent.quest_streak)));
      }
      // Also celebrate when the OTHER user completes the quest — feels
      // more alive and social.
      setConfettiKey((k) => k + 1);
      if (wsEvent.unlocked) {
        setUnlockedTheme(wsEvent.unlocked);
        setTimeout(() => setUnlockedTheme(null), 6000);
      }
    }
  }, [wsEvent, conversationId]);

  if (!event) return null;

  const dismiss = () => {
    const dkey = `surprise-dismissed-${conversationId}-${new Date().toISOString().slice(0, 10)}`;
    localStorage.setItem(dkey, "1");
    setDismissed(true);
  };

  const ackLost = () => {
    const lostKey = `streak-lost-ack-${conversationId}`;
    localStorage.setItem(lostKey, String(streakLost));
    setLostDismissed(true);
  };

  const doIt = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/surprise/complete`);
      setCompleted(true);
      setCompletedBy(data.completed_by || null);
      setAtRisk(false);
      if (data.quest_streak != null) {
        setStreak(Number(data.quest_streak));
        setStreakMax((s) => Math.max(s, Number(data.quest_streak)));
      }
      // Fire confetti burst — the "Yap" tap completing a quest is a
      // celebration moment and the previous flash-only feedback was easy
      // to miss on mobile.
      setConfettiKey((k) => k + 1);
      if (data.xp_gained || data.love_gained) {
        setRewardFlash({ xp: data.xp_gained, love: data.love_gained });
        setTimeout(() => setRewardFlash(null), 3500);
      }
      if (data.unlocked) {
        setUnlockedTheme(data.unlocked);
        setTimeout(() => setUnlockedTheme(null), 6000);
      }
    } catch (e) {
      alert(formatApiError(e));
    }
    setSubmitting(false);
  };

  const showStreak = streak > 0;
  const streakPct = Math.min(100, Math.round((streak / streakGoal) * 100));

  return (
    <div className="space-y-2">
      {confettiKey > 0 && <Confetti fire={confettiKey} />}
      {/* Streak lost banner — appears once per lost event */}
      <AnimatePresence>
        {streakLost > 0 && !lostDismissed && (
          <motion.div
            key="lost"
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, height: 0, scale: 0.9 }}
            className="mx-3 mt-3 border-hard shadow-hard rounded-2xl bg-[#FFDDD5] overflow-hidden"
            data-testid="streak-lost-banner"
          >
            <div className="flex items-center gap-3 p-3">
              <HeartCrack strokeWidth={3} className="w-6 h-6 text-[#B03A2E] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-base leading-tight">Serin sıfırlandı</div>
                <div className="text-xs text-[#333]">
                  {streakLost} günlük görev serini kaçırdın. Bugün görevi yaparak yeniden başlat 💪
                </div>
              </div>
              <button
                data-testid="streak-lost-ack"
                onClick={ackLost}
                className="bg-[#0A0A0A] text-white border-hard rounded-xl px-3 py-1.5 text-[11px] font-black uppercase tracking-widest press-btn shrink-0"
              >
                Tamam
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main surprise card */}
      <AnimatePresence>
        {!dismissed && (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: -12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className={`mx-3 mt-3 border-hard shadow-hard rounded-2xl overflow-hidden ${
              completed
                ? "bg-gradient-to-r from-[#CCFF00] to-[#E7FFCF]"
                : atRisk
                  ? "bg-gradient-to-r from-[#FFD0C2] to-[#FFE7DC]"
                  : "bg-gradient-to-r from-[#FFF7C2] to-[#FFE7CC]"
            } ${!completed && !atRisk ? "quest-pending-pulse" : ""}`}
            data-testid="surprise-card"
          >
            {/* At-risk banner strip (inside main card) */}
            {atRisk && !completed && (
              <div className="bg-[#B03A2E] text-white px-3 py-1.5 flex items-center gap-2 border-b-2 border-black" data-testid="streak-at-risk-banner">
                <AlertTriangle strokeWidth={3} className="w-4 h-4 shrink-0" />
                <div className="text-[11px] font-black uppercase tracking-widest">
                  Serin bugün kırılabilir — {streak} gün emeğini kaybetme!
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3">
              <motion.div key={completed ? "done" : "todo"} initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="text-3xl">
                {completed ? "✅" : event.emoji}
              </motion.div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] uppercase font-black tracking-widest bg-[#0A0A0A] text-white rounded-full px-2 py-0.5">
                    <Gift strokeWidth={3} className="inline w-3 h-3 mr-1" /> Günün Görevi
                  </div>
                  {completed && completedBy && (
                    <div className="text-[10px] uppercase font-black tracking-widest bg-white border-hard-2 rounded-full px-2 py-0.5" data-testid="surprise-completed-by">
                      <Check strokeWidth={3} className="inline w-3 h-3 mr-0.5" /> {completedBy.name}
                    </div>
                  )}
                  {showStreak && (
                    <motion.div
                      key={streak}
                      initial={{ scale: 0.7, rotate: -8 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 12 }}
                      className="text-[10px] uppercase font-black tracking-widest bg-[#FF4E00] text-white rounded-full px-2 py-0.5 inline-flex items-center gap-1"
                      data-testid="surprise-streak-badge"
                      title={`En uzun seri: ${streakMax} gün`}
                    >
                      <Flame strokeWidth={3} className="w-3 h-3" /> {streak} GÜN SERİ
                    </motion.div>
                  )}
                </div>
                <div className="font-display text-lg mt-0.5 truncate">{event.title}</div>
                <div className="text-xs text-[#555]">{event.desc}</div>

                {showStreak && streak < streakGoal && (
                  <div className="mt-1.5 flex items-center gap-2" data-testid="surprise-streak-progress">
                    <div className="flex-1 h-1.5 bg-white/60 border-hard-2 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${streakPct}%` }}
                        transition={{ duration: 0.6 }}
                        className="h-full bg-[#FF4E00]"
                      />
                    </div>
                    <span className="text-[10px] font-black text-[#333] uppercase tracking-wider whitespace-nowrap">
                      {streak}/{streakGoal} · Aurora
                    </span>
                  </div>
                )}

                {rewardFlash && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-1 text-[11px] font-black inline-flex items-center gap-2 bg-white border-hard-2 rounded-full px-2 py-0.5"
                    data-testid="surprise-reward-flash"
                  >
                    <Sparkles strokeWidth={3} className="w-3 h-3" /> +{rewardFlash.xp} XP {rewardFlash.love > 0 ? `• +${rewardFlash.love} 💖` : ""}
                  </motion.div>
                )}
              </div>

              {!completed ? (
                <button
                  data-testid="surprise-do-btn"
                  onClick={doIt}
                  disabled={submitting}
                  className="bg-[#0A0A0A] text-white border-hard rounded-xl px-3 py-2 text-xs font-black uppercase tracking-widest press-btn shadow-hard-sm disabled:opacity-60 shrink-0"
                >
                  {submitting ? "…" : "Yap"}
                </button>
              ) : (
                <div className="bg-white border-hard-2 rounded-xl px-3 py-2 text-xs font-black uppercase tracking-widest shrink-0">Tamamlandı</div>
              )}

              <button data-testid="surprise-dismiss" onClick={dismiss} className="w-8 h-8 border-hard-2 rounded-lg flex items-center justify-center press-btn shrink-0" aria-label="Kapat">
                <X strokeWidth={3} className="w-3.5 h-3.5" />
              </button>
            </div>

            <AnimatePresence>
              {unlockedTheme && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-[#0F1F3A] text-[#7EF9C4] border-t-2 border-black overflow-hidden"
                  data-testid="surprise-unlock-banner"
                >
                  <div className="flex items-center gap-2 p-3">
                    <Trophy strokeWidth={3} className="w-5 h-5" />
                    <div className="flex-1">
                      <div className="font-display text-base">Aurora Teması açıldı!</div>
                      <div className="text-[11px] uppercase tracking-widest opacity-80">14 günlük serini tamamladın — dünyalar panelinden seç</div>
                    </div>
                    <Sparkles strokeWidth={3} className="w-4 h-4" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
